import React, { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLightWithPlace from "@/utils/EtContentEditorLightWithPlace"
import { useGetShowcaseTypesQuery, useUpdateShowcaseTypeMutation } from "./showcaseTypesApiSlice"

const EtUpdateShowcaseType = ({id, setActiveAdminView}) => {
  // Created - 02 Dec 2024
  // Author - Atul Pandey
  // Updated - 27 Feb 2025 - updated the function with props - { id, setActiveAdminView}
  // Updated - 27 Feb 2025 - switched from navigate to setActiveAdminView

  // 1 - Set Title
  useTitle("Update Showcase Type")

  // 2 - extract the showcaseType Id from the url
  // const { id } = useParams()
  // //console.log("id:", id)

  // 3 - Extract the showcaseType from the query to load the showcaseType data
  const { showcaseType } = useGetShowcaseTypesQuery("ShowcaseType", {
    selectFromResult: ({ data }) => ({
      showcaseType: data?.entities[id],
    }),
  })

  //console.log('showcase type:', showcaseType)

  // 4 - Check for returned data
  if (!showcaseType) {
    return <PulseLoader color={"#ea580c"} />
  }

  // 5. Display data
  let content
  content = <EtUpdateShowcaseTypeForm showcaseType={showcaseType} id={id} setActiveAdminView={setActiveAdminView}/> // 27 Feb 2025 - added setActiveAdminView={setActiveAdminView}
  return content
}

const EtUpdateShowcaseTypeForm = ({ showcaseType, id, setActiveAdminView}) =>{
  // 1 - set the variable for navigate
  const navigate = useNavigate()

  //console.log('update form invoked!')

  // 2 - set the mutation to create the new section data
  const[updateShowcaseType, {
    isLoading,
    isSuccess,
    isError,
    error,
  }] = useUpdateShowcaseTypeMutation()

// 3 - set the form data object
const [formData, setFormData] = useState({
  showcaseTypeName: showcaseType.showcaseTypeName,
  showcaseTypeDesc: showcaseType.showcaseTypeDesc,
  aboutMe: showcaseType.aboutMe,
})

//console.log('formData for update:', formData)

// 4 - Reset form upon successful submission
useEffect(() => {
  if(isSuccess) {
    setFormData({
      showcaseTypeName: "",
      showcaseTypeDesc: "",
      aboutMe: "",
      pic: "",
    })

    // navigate('/showcasetypes')
    setActiveAdminView('showcaseTypes'); // 27 Feb 2025
  }

},[isSuccess]) // ✅ 27 Feb 2025 - Removed `navigate` since it's no longer used.

// 5 - Form field updates / change handling

const handleChange = (e) => {
  const { name, value, files } = e.target
  if (name === "pic") {
    setFormData(prevState => ({ ...prevState, pic: files[0] }))
  } else {
    setFormData(prevState => ({ ...prevState, [name]: value }))
  }
}

// 6 - function to strip html tags from sections
function stripHtml(html) {
  return html.replace(/<\/?[^>]+(>|$)/g, "");
}

// 7 - Form Submit Logic
const handleSubmit = async (e) => {
e.preventDefault()

// Initialize an object to collect the form data
const showcaseTypeData = {
  showcaseTypeName: formData.showcaseTypeName,
  showcaseTypeDesc: formData.showcaseTypeDesc,
  aboutMe: formData.aboutMe,
}

//console.log('showcaseType Name:',showcaseTypeData.showcaseTypeName)

if (formData.pic) {
  // Convert the image to a base64 string
  const reader = new FileReader()
  reader.onloadend = async () => {
    // Once conversion is done, add the base64 string to your data object
    showcaseTypeData.pic = reader.result

    // Now, send the data object to the server
    try {
      showcaseTypeData.id = id
      console.log("Form submitted.")
      await updateShowcaseType(showcaseTypeData).unwrap() // Dispatch the RTK Query mutation
    } catch (error) {
      console.error(error)
    }
  }
  reader.readAsDataURL(formData.pic)
} else {
  // No image, send other data as is
  try {
    showcaseTypeData.id = id
    console.log("Form submitted.")
    await updateShowcaseType(showcaseTypeData).unwrap() // Dispatch the RTK Query mutation
  } catch (error) {
    console.error(error)
  }
}
}

// 8 - Handle Cancel

const handleCancel = () => {
  // navigate("/showcasetypes")
  setActiveAdminView('showcaseTypes'); // 27 Feb 2025
}

// 9 - Handle Clear
const handleClear = () => {
  setFormData({
    showcaseTypeName: "",
    showcaseTypeDesc: "",
    aboutMe: "",
    pic: "",
    })
}

return (
  <div className="bg-gray-200 p-1 w-3/4 mx-auto mt-5 rounded-lg shadow-md shadow-black">
    {isLoading && <PulseLoader color={"#ea580c"} />}
    {isError && (
      <p className="text-orange-700 font-semi-bold py-2">
        {error?.data?.message}
      </p>
    )}
    <div className="bg-white p-1 m-1 rounded">
      <p className="p-1 m-1 font-bold font-poppins text-xl">Update a Showcase Type</p>

      <div className="bg-gray-200 rounded m-1 p-1">
        <form
          onSubmit={handleSubmit}
          className="m-1 p-1 text-xs font-poppins"
        >
          
          <div id="showcaseTypeName">
            <input
              type="text"
              name="showcaseTypeName"
              placeholder="Enter the showcase type name here"
              value={formData.showcaseTypeName}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />
          </div>

          <div className="bg-white w-full rounded p-2 m-1">
            <EtContentEditorLightWithPlace
              value={formData.showcaseTypeDesc}
              handleChange={(content) =>
                setFormData({ ...formData, showcaseTypeDesc: content })
              }
              placeholderText={
                "Write this showcase type is about or will be used for"
              }
              // toolbarId={'sectionDesc'}
            />
          </div>

          <div id="aboutMe">
            <input
              type="text"
              name="aboutMe"
              placeholder="Add something about the personality about this showcase type"
              value={formData.aboutMe}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />
          </div>

          <div id="pic">
            <input
              type="file"
              name="pic"
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />
          </div>

         <div id="showcases" className="bg-white m-1 px-1 py-3 rounded">
            <EtBtnFormBasic
              name={"Update"}
              type={"create"}
              handleClick={handleSubmit}
            />
            <EtBtnFormBasic
              name={"Clear"}
              type={"clear"}
              handleClick={handleClear}
            />
            <EtBtnFormBasic
              name={"Cancel"}
              type={"cancel"}
              handleClick={handleCancel}
            />
          </div>
        </form>
      </div>
    </div>
  </div>
)

}

export default EtUpdateShowcaseType